import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { VideoSearchActions } from './video-search.actions';
import {
  selectSearchResults,
  selectIsLinkVisible,
  selectPrimaryResult,
  selectHasResults
} from './video-search.selectors';
import { VideoSearchResult, VideoSearchResultsPayload } from './models/video-search.models';

/**
 * Facade service for Video Search state management.
 * Components should use this instead of directly accessing the store.
 */
@Injectable({
  providedIn: 'root'
})
export class VideoSearchFacade {
  
  // Observables
  readonly searchResults$: Observable<VideoSearchResult[]>;
  readonly linkVisible$: Observable<boolean>;
  readonly primaryResult$: Observable<VideoSearchResult | null>;
  readonly hasResults$: Observable<boolean>;

  constructor(private store: Store) {
    this.searchResults$ = this.store.select(selectSearchResults);
    this.linkVisible$ = this.store.select(selectIsLinkVisible);
    this.primaryResult$ = this.store.select(selectPrimaryResult);
    this.hasResults$ = this.store.select(selectHasResults);
  }

  /**
   * Processes video search results from WebSocket
   */
  handleSearchResults(payload: VideoSearchResultsPayload): void {
    this.store.dispatch(VideoSearchActions.handleSearchResults({ payload }));
  }

  /**
   * Shows the video link (automatically called when results are received)
   */
  showLink(reason?: string): void {
    this.store.dispatch(VideoSearchActions.showLink({ reason }));
  }

  /**
   * Shows link after speech ends (for subtitle-enabled mode)
   */
  showLinkAfterSpeech(reason?: string): void {
    this.store.dispatch(VideoSearchActions.showLinkAfterSpeech({ reason }));
  }

  /**
   * Hides the video link (typically when user clicks it)
   */
  hideLink(reason?: string): void {
    this.store.dispatch(VideoSearchActions.hideLink({ reason }));
  }

  /**
   * Clears current search results
   */
  clearResults(reason?: string): void {
    this.store.dispatch(VideoSearchActions.clearResults({ reason }));
  }
}
